<template>
  <UDashboardPanel id="nols-import" :ui="{ body: 'h-full w-full sm:p-0 p-0' }">
    <!--
    <template #header>
      <UDashboardNavbar :title="t('nolsImport.title')">
        <template #leading>
          <UDashboardSidebarCollapse />
        </template>
        <template #right>
          <LanguageSwitcher />
        </template>
      </UDashboardNavbar>
    </template>
    -->
    
    <template #body>
      <div class="w-full h-full flex flex-col">
        <div class="px-4 pt-4 pb-4">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <UInput
              v-model="searchQuery"
              class="w-full sm:w-80"
              icon="i-lucide-search"
              :placeholder="t('nolsImport.table.search')"
              :ui="{ trailing: 'pe-1' }"
            >
              <template v-if="searchQuery?.length" #trailing>
                <UButton
                  color="neutral"
                  variant="link"
                  size="sm"
                  icon="i-lucide-circle-x"
                  :aria-label="t('workflows.table.filters.clear')"
                  @click="searchQuery = ''"
                />
              </template>
            </UInput>
          </div>
        </div>
        
        <div v-if="status === 'success' && data.length === 0" class="flex flex-col items-center justify-center py-20 text-center">
          <UIcon name="i-lucide-inbox" class="text-5xl text-(--ui-text-dimmed) mb-4" />
          <h3 class="text-lg font-semibold text-(--ui-text)">{{ t('nolsImport.empty.title') }}</h3>
          <p class="text-sm text-(--ui-text-muted) mt-1">{{ t('nolsImport.empty.description') }}</p>
        </div>
        
        <div v-else class="flex-1 flex flex-col overflow-hidden">
          <div class="flex-1 min-h-0 pb-[115px] lg:pb-0">
            <UTable
              :columns="columns"
              :data="tableData"
              sticky
              @hover="() => {}"
              class="w-full h-full px-4"
              :ui="{
                base: 'table-fixed border-separate border-spacing-0',
                thead: '[&>tr]:bg-(--ui-bg-elevated)/50 [&>tr]:after:content-none',
                tbody: '[&>tr]:last:[&>td]:border-b-0',
                th: 'py-2 first:rounded-l-lg last:rounded-r-lg border-y border-(--ui-border) first:border-l last:border-r',
                td: 'border-b border-(--ui-border)'
              }"
            />
          </div>
          
          <div class="fixed bottom-0 left-0 right-0 lg:sticky bg-(--ui-bg) border-t border-(--ui-border) px-4 py-3.5 z-10">
            <div class="flex items-center justify-between gap-3 min-h-8">
              <div class="text-sm text-(--ui-text-muted)">
                <span v-if="pagination">
                  <span class="hidden lg:inline">{{ t('nolsImport.table.showing') }}</span> {{ ((pagination.currentPage - 1) * pagination.pageSize) + 1 }} - {{ Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems) }} <span class="hidden lg:inline">{{ t('nolsImport.table.of') }}</span><span class="lg:hidden">/</span> {{ pagination.totalItems }}
                </span>
                <span v-else>{{ t('nolsImport.table.loading') }}</span>
              </div>
              
              <div class="flex items-center gap-3">
                <USelect
                  v-model="selectedPageSize"
                  :items="pageSizeOptions"
                  size="sm"
                  class="w-20 cursor-pointer"
                  @update:model-value="handlePageSizeChange"
                />
                <UPagination
                  v-if="pagination && pagination.totalItems > pagination.pageSize"
                  v-model:page="currentPaginationPage"
                  :total="pagination.totalItems"
                  :items-per-page="pagination.pageSize"
                  :sibling-count="1"
                  size="sm"
                  class="lg:hidden"
                  :ui="{
                    item: 'cursor-pointer',
                    prev: 'cursor-pointer',
                    next: 'cursor-pointer',
                    first: 'hidden',
                    last: 'hidden'
                  }"
                />
                <UPagination
                  v-if="pagination && pagination.totalItems > pagination.pageSize"
                  v-model:page="currentPaginationPage"
                  :total="pagination.totalItems"
                  :items-per-page="pagination.pageSize"
                  :sibling-count="2"
                  size="sm"
                  class="hidden lg:flex"
                  :ui="{
                    item: 'cursor-pointer',
                    prev: 'cursor-pointer',
                    next: 'cursor-pointer',
                    first: 'cursor-pointer',
                    last: 'cursor-pointer'
                  }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </UDashboardPanel>
  
  <JsonPreviewModal
    v-if="isPreviewOpen"
    v-model="isPreviewOpen"
    :file-name="previewFileName"
    :file-content="previewFileContent"
    :is-validating="isValidating && validatingFileName === previewFileName"
    @validate="validateFile"
  />
  
  <ValidationResultModal
    v-model="showValidationModal"
    :validation-result="validationResult"
  />
</template>

<script setup lang="ts">
import { computed, h, onMounted, ref, resolveComponent, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { api, type NolsFileInfoDto } from '@/api/eOrdersApi'
import { useApiAdapter, usePreferences } from '@/store'
import { useNolsImportData } from '@/composables/useNolsImportData'
import { formatDate } from '@/utils/date'
import { debounce, showError } from '@/utils/utils'
import { formatFileSize } from '@/utils/fileUtils'
import { useNolsValidation } from '@/composables/useNolsValidation'
import JsonPreviewModal from '@/components/nols/JsonPreviewModal.vue'
import ValidationResultModal from '@/components/nols/ValidationResultModal.vue'

const UButton = resolveComponent('UButton')

const { t, locale } = useI18n()
const apiAdapter = useApiAdapter()
const preferences = usePreferences()

const { data, status, pagination, fetchFiles, applyFilters, goToPage, changePageSize } = useNolsImportData()
const { isValidating, validatingFileName, validationResult, showValidationModal, validateFile } = useNolsValidation()

const isPreviewOpen = ref(false)
const previewFileName = ref('')
const previewFileContent = ref('')

const searchQuery = ref('')
const selectedPageSize = ref(preferences.nolsImportPageSize)
const currentPaginationPage = ref(1)

const pageSizeOptions = [
  { label: '20', value: 20 },
  { label: '40', value: 40 },
  { label: '50', value: 50 },
  { label: '100', value: 100 }
]

const debouncedApplyFilters = debounce(async (filters: { search?: string }) => {
  await applyFilters(filters)
}, 400)

watch(searchQuery, (newQuery) => {
  debouncedApplyFilters({ search: newQuery })
})

const skeletonData = computed(() => {
  if (status.value !== 'pending') return []
  
  return Array.from({ length: 5 }, (_, index) => ({
    name: `skeleton-${index}`,
    createdUtc: '',
    sizeBytes: 0,
    _skeleton: true
  }))
})

const tableData = computed(() =>
  status.value === 'pending' ? skeletonData.value : data.value
)

const columns = computed(() => [
  {
    id: 'name',
    header: t('nolsImport.table.fileName'),
    cell: ({ row }) => {
      if (row.original._skeleton) {
        return h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-4 rounded w-60' })
      }
      
      return h('span', { class: 'text-sm font-medium text-(--ui-text)' }, row.original.name)
    }
  },
  {
    id: 'createdUtc',
    header: t('nolsImport.table.created'),
    cell: ({ row }) => {
      if (row.original._skeleton) {
        return h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-4 rounded w-32' })
      }
      
      return h('span', { class: 'text-sm text-(--ui-text-muted)' },
        formatDate(row.original.createdUtc, 'datetime', locale.value)
      )
    }
  },
  {
    id: 'sizeBytes',
    header: t('nolsImport.table.size'),
    cell: ({ row }) => {
      if (row.original._skeleton) {
        return h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-4 rounded w-16' })
      }
      
      return h('span', { class: 'text-sm text-(--ui-text-muted)' },
        formatFileSize(row.original.sizeBytes)
      )
    }
  },
  {
    id: 'actions',
    header: t('nolsImport.table.actions'),
    cell: ({ row }) => {
      if (row.original._skeleton) {
        return h('div', { class: 'animate-pulse bg-(--ui-bg-accented) h-7 w-16 rounded' })
      }
      
      return h('div', { class: 'flex items-center gap-1' }, [
        h(UButton, {
          icon: 'i-lucide-eye',
          variant: 'ghost',
          size: 'sm',
          title: t('nolsImport.actions.preview'),
          class: 'cursor-pointer',
          onClick: () => openPreview(row.original)
        }),
        h(UButton, {
          icon: 'i-lucide-file-check',
          variant: 'ghost',
          size: 'sm',
          title: t('nolsImport.actions.validate'),
          class: 'cursor-pointer',
          loading: isValidating.value && validatingFileName.value === row.original.name,
          onClick: () => validateFile(row.original.name)
        }),
        h(UButton, {
          icon: 'i-lucide-download',
          variant: 'ghost',
          size: 'sm',
          title: t('nolsImport.actions.download'),
          class: 'cursor-pointer',
          onClick: () => downloadFile(row.original)
        })
      ])
    }
  }
])

const openPreview = async (file: NolsFileInfoDto) => {
  try {
    const content = await apiAdapter.invoke(config => api.nolsDownloadFile(file.name, config))
    
    if (content === undefined) return
    
    previewFileName.value = file.name
    previewFileContent.value = typeof (content as unknown) === 'string' ? content : JSON.stringify(content, null, 2)
    isPreviewOpen.value = true
  } catch {
    showError(t('nolsImport.error.previewFailed'))
  }
}

const downloadFile = async (file: NolsFileInfoDto) => {
  let url: string | null = null
  
  try {
    const content = await apiAdapter.invoke(config => api.nolsDownloadFile(file.name, config))
    
    if (content === undefined) return
    
    const json = typeof (content as unknown) === 'string' ? content : JSON.stringify(content, null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    
    link.href = url
    link.download = file.name
    document.body.appendChild(link)
    link.click()
    
    document.body.removeChild(link)
  } catch {
    showError(t('nolsImport.error.downloadFailed'))
  } finally {
    if (url) window.URL.revokeObjectURL(url)
  }
}

const handlePageChange = async (page: number) => {
  if (!pagination.value) return
  if (page < 1 || page > pagination.value.totalPages) return
  
  await goToPage(page)
}

const handlePageSizeChange = async (newSize: number) => {
  await changePageSize(newSize)
  
  selectedPageSize.value = newSize
  currentPaginationPage.value = 1
  preferences.setNolsImportPageSize(newSize)
}

watch(pagination, newPagination => {
  if (!newPagination) return
  
  if (typeof newPagination.pageSize === 'number') {
    selectedPageSize.value = newPagination.pageSize
  }
  
  if (typeof newPagination.currentPage === 'number') {
    currentPaginationPage.value = newPagination.currentPage
  }
}, { immediate: true })

watch(currentPaginationPage, async (newPage, oldPage) => {
  if (newPage === oldPage) return
  
  if (newPage !== pagination.value?.currentPage) {
    await handlePageChange(newPage)
  }
})

onMounted(async () => {
  await fetchFiles(selectedPageSize.value, currentPaginationPage.value)
})
</script>
